import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type FontSize = 'small' | 'normal' | 'large' | 'extra-large';

interface FontSizeContextType {
  fontSize: FontSize;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

const fontSizeMap: Record<FontSize, string> = {
  'small': 'text-sm',
  'normal': 'text-base',
  'large': 'text-lg', 
  'extra-large': 'text-xl'
};

const fontSizeOrder: FontSize[] = ['small', 'normal', 'large', 'extra-large'];

export const FontSizeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [fontSize, setFontSize] = useState<FontSize>(() => {
    if (typeof window === 'undefined') return 'normal';
    
    const saved = localStorage.getItem('fontSize') as FontSize;
    return saved && fontSizeOrder.includes(saved) ? saved : 'normal';
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Retirer toutes les classes de taille de police du body
    Object.values(fontSizeMap).forEach(cls => {
      document.body.classList.remove(cls);
    });
    
    // Ajouter la classe de taille actuelle
    document.body.classList.add(fontSizeMap[fontSize]);
    
    // Sauvegarder dans localStorage
    localStorage.setItem('fontSize', fontSize);
  }, [fontSize]);

  const increaseFontSize = () => {
    const currentIndex = fontSizeOrder.indexOf(fontSize);
    if (currentIndex < fontSizeOrder.length - 1) {
      setFontSize(fontSizeOrder[currentIndex + 1]);
    }
  };

  const decreaseFontSize = () => {
    const currentIndex = fontSizeOrder.indexOf(fontSize);
    if (currentIndex > 0) {
      setFontSize(fontSizeOrder[currentIndex - 1]);
    }
  };

  const resetFontSize = () => {
    setFontSize('normal');
  };

  const value = React.useMemo(() => ({
    fontSize,
    increaseFontSize,
    decreaseFontSize,
    resetFontSize
  }), [fontSize]);

  return (
    <FontSizeContext.Provider value={value}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (context === undefined) {
    throw new Error('useFontSize must be used within a FontSizeProvider');
  }
  return context;
};